/**
 * Family Communications Hub
 *
 * Tab 1 (default — daily use): Child Engagement Hub
 * Tab 2 (messages): Parent Messages — director inbox
 * Tab 3 (portal management): Family SMS Access Portal
 *
 * NOTE: The old "Enrollment Forms" tab was moved to a standalone Form Builder
 * sidebar hub (`/form-builder`). Legacy `/forms` URL still redirects there.
 */
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import AppShell from "@/components/layout/AppShell";
import CenterCommunicationTab from "@/components/communications/CenterCommunicationTab";
import { ParentDirectorInbox } from "@/components/parent-communication/ParentDirectorInbox";
import ParentCommunicationSettingsPage from "@/pages/ParentCommunicationSettingsPage";
import PlatformRevenueTab from "@/components/communications/PlatformRevenueTab";
import { useAuth } from "@/contexts/AuthContext";
import { Megaphone, Settings2, MessageSquare, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BADGE_POLL_MS = 30_000; // poll for unread count every 30 s

function useActiveTab() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const raw = params.get("tab");
  const initial = raw === "portal" ? "portal" : raw === "messages" ? "messages" : raw === "revenue" ? "revenue" : "send";
  const [tab, setTabState] = useState(initial);

  const setTab = (t) => {
    setTabState(t);
    const query = t === "portal" ? "?tab=portal" : t === "messages" ? "?tab=messages" : t === "revenue" ? "?tab=revenue" : "";
    navigate(`/family-communications${query}`, { replace: true });
  };

  return [tab, setTab];
}

export default function FamilyCommunicationsHubPage() {
  const [activeTab, setActiveTab] = useActiveTab();
  const { user } = useAuth();
  const [permMatrix, setPermMatrix] = useState({});
  const [inboxUnread, setInboxUnread] = useState(0);
  const badgePollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/owner/permissions`)
      .then(r => setPermMatrix(r.data?.permissions || {}))
      .catch(() => {});
  }, []);

  // Poll unread count when NOT on the messages tab so the badge updates in background
  const fetchUnread = async () => {
    try {
      const res = await axios.get(`${API}/parent-communication/director/messages/unread-count`);
      setInboxUnread(res.data?.unread || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    // Fetch immediately on mount
    fetchUnread();
    badgePollRef.current = setInterval(fetchUnread, BADGE_POLL_MS);
    return () => clearInterval(badgePollRef.current);
  }, []); // eslint-disable-line

  const canAccess = (key) => {
    if (user?.role === 'owner') return true;
    const rp = permMatrix[key];
    if (!rp) return true;
    return rp[user?.role] !== false;
  };

  const showPortal = canAccess('family_sms_portal');
  const showMessages = canAccess('family_sms_portal'); // same gate as portal
  const showRevenue = user?.isPrimaryOwner === true;

  return (
    <AppShell pageTitle="Family Communications Hub" breadcrumb>
      {/* Tab switcher */}
      <div className="flex flex-wrap gap-1 mb-6 bg-slate-100 rounded-2xl p-1 w-fit" data-testid="family-hub-tabs">
        {/* Tab 1 — Child Engagement Hub */}
        <button
          type="button"
          onClick={() => setActiveTab("send")}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            activeTab === "send" ? "bg-white shadow text-teal-700" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="hub-tab-send"
        >
          <Megaphone className="w-4 h-4" />
          Child Engagement Hub
        </button>

        {/* Tab 2 — Parent Messages */}
        {showMessages && (
          <button
            type="button"
            onClick={() => setActiveTab("messages")}
            className={`relative flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === "messages" ? "bg-white shadow text-violet-700" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="hub-tab-messages"
          >
            <MessageSquare className="w-4 h-4" />
            Parent Messages
            {inboxUnread > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-violet-600 text-white text-[10px] font-bold leading-none"
                data-testid="hub-tab-messages-badge"
              >
                {inboxUnread}
              </span>
            )}
          </button>
        )}

        {/* Tab 3 — Family SMS Access Portal */}
        {showPortal && (
          <button
            type="button"
            onClick={() => setActiveTab("portal")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === "portal" ? "bg-white shadow text-emerald-700" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="hub-tab-portal"
          >
            <Settings2 className="w-4 h-4" />
            Family SMS Access Portal
          </button>
        )}

        {/* Tab 4 — Platform Revenue (owner + business manager when granted) */}
        {showRevenue && (
          <button
            type="button"
            onClick={() => setActiveTab("revenue")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === "revenue" ? "bg-white shadow text-amber-700" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="hub-tab-revenue"
            title="Platform fees earned. Access controlled in Owner Tools → Permissions."
          >
            <DollarSign className="w-4 h-4" />
            Platform Revenue
          </button>
        )}
      </div>

      {activeTab === "send" && <CenterCommunicationTab />}
      {activeTab === "messages" && showMessages && (
        <ParentDirectorInbox onUnreadCount={setInboxUnread} />
      )}
      {activeTab === "portal" && showPortal && <ParentCommunicationSettingsPage embedded />}
      {activeTab === "revenue" && showRevenue && <PlatformRevenueTab />}
    </AppShell>
  );
}
